import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const PERIODS = ["daily", "weekly", "monthly"];
const DIMS = ["DATA_COVERAGE", "EXPERIMENT_QUALITY", "EVIDENCE_DEPTH", "ROBUSTNESS", "LIVE_EVIDENCE", "UNRESOLVED_QUESTIONS"];
const LEVEL_CLS = { STRONG: "text-emerald-400 border-emerald-800", MODERATE: "text-sky-300 border-sky-800", WEAK: "text-amber-300 border-amber-800", NONE: "text-slate-500 border-slate-700" };

const KV = ({ label, value, testId }) => (
  <div><div className="metric-label">{label}</div>
    <div className="text-slate-200 mt-0.5 font-mono text-xs" data-testid={testId}>{value}</div></div>
);

const List = ({ items, testId }) => (
  <ul className="space-y-1 text-xs font-mono text-slate-400" data-testid={testId}>
    {items.map((it, i) => <li key={i} className="leading-snug">· {it}</li>)}
    {items.length === 0 && <li className="text-slate-600">none this period</li>}
  </ul>
);

export default function LearningReport() {
  const [period, setPeriod] = useState("weekly");
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/reports/learning/latest?period=${period}`)
      .then((r) => setReport(r.data)).catch(() => setReport(null));
    api.get("/reports/learning/history").then((r) => setHistory(r.data.reports)).catch(() => {});
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reports/learning/generate", { period });
      setReport(r.data);
      toast.success("Learning report generated");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const s = report?.sections;

  return (
    <div className="space-y-4" data-testid="learning-report-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100 tracking-tight">Hussar Lab — Learning Report</h1>
          <p className="text-xs font-mono text-slate-500 mt-1">
            What Hussar learned · what changed because of it · what remains uncertain · which evidence is strongest
          </p>
        </div>
        <div className="flex items-center gap-2">
          {PERIODS.map((p) => (
            <Button key={p} size="sm" variant="outline" data-testid={`period-${p}`}
              onClick={() => setPeriod(p)}
              className={`text-[10px] h-7 px-3 font-mono ${period === p ? "border-sky-700 text-sky-300" : "border-slate-700 text-slate-400"}`}>
              {p.toUpperCase()}</Button>))}
          <Button size="sm" data-testid="generate-report-button" disabled={busy} onClick={generate}
            className="bg-slate-100 text-slate-900 hover:bg-white text-[10px] h-7 px-3 font-mono">GENERATE</Button>
        </div>
      </div>

      {!report && (
        <Panel title="No report yet" testId="learning-report-empty">
          <p className="text-xs font-mono text-slate-500">Generate a {period} report to aggregate the research corpus.</p>
        </Panel>)}

      {report && (
        <>
          <div className={`text-[10px] font-mono border rounded-sm px-3 py-2 ${report.production_impact.startsWith("none") ? "text-slate-400 border-slate-700" : "text-amber-300 border-amber-800"}`}
            data-testid="production-impact">
            PRODUCTION IMPACT: {report.production_impact}
          </div>

          <Panel title="Learning Scorecard — dimensions, never a single score" testId="learning-scorecard">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {DIMS.map((d) => (
                <div key={d} className="border border-slate-800 rounded-sm p-2" data-testid={`scorecard-${d}`}>
                  <div className="text-[9px] font-mono text-slate-500">{d.replace(/_/g, " ")}</div>
                  <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 border rounded-sm font-mono ${LEVEL_CLS[report.scorecard[d].level]}`}
                    data-testid={`scorecard-level-${d}`}>{report.scorecard[d].level}</span>
                  <div className="text-[10px] font-mono text-slate-400 mt-1 leading-snug">{report.scorecard[d].detail}</div>
                </div>))}
            </div>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="New Data Ingested" testId="section-new-data">
              <div className="grid grid-cols-3 gap-3">
                <KV label="Snapshots" value={s.new_data_ingested.snapshots} testId="data-snapshots" />
                <KV label="Sources" value={s.new_data_ingested.sources.join(", ") || "none"} testId="data-sources" />
                <KV label="Evidence tiers" value={s.new_data_ingested.evidence_tiers.join(", ") || "none"} testId="data-tiers" />
              </div>
              {s.new_data_ingested.stale_sources.length > 0 && (
                <p className="text-[10px] font-mono text-amber-300 mt-2" data-testid="data-stale">
                  STALE: {s.new_data_ingested.stale_sources.join(", ")}</p>)}
            </Panel>

            <Panel title="Observations & Hypotheses" testId="section-observations">
              <div className="grid grid-cols-3 gap-3">
                <KV label="New observations" value={s.observations.new} testId="obs-new" />
                <KV label="New hypotheses" value={s.hypotheses.new} testId="hyp-new" />
                <KV label="AI-derived" value={s.hypotheses.ai_derived} testId="hyp-ai" />
              </div>
            </Panel>

            <Panel title="Experiments" testId="section-experiments">
              <div className="grid grid-cols-4 gap-3">
                <KV label="Completed" value={s.experiments.completed} testId="exp-completed" />
                <KV label="Failed" value={s.experiments.failed} testId="exp-failed" />
                <KV label="Eligible" value={s.experiments.eligible} testId="exp-eligible" />
                <KV label="Robustness" value={JSON.stringify(s.experiments.robustness)} testId="exp-robustness" />
              </div>
            </Panel>

            <Panel title="AI Research Council" testId="section-council">
              <div className="grid grid-cols-4 gap-3">
                <KV label="Critiques" value={s.ai_research_council.critiques} testId="council-critiques" />
                <KV label="Evidence-backed" value={s.ai_research_council.evidence_backed} testId="council-backed" />
                <KV label="Unsupported" value={s.ai_research_council.unsupported} testId="council-unsupported" />
                <KV label="Needs testing" value={s.ai_research_council.needs_testing} testId="council-needs" />
              </div>
            </Panel>

            <Panel title="Hussar Shadow (modeled counterfactuals)" testId="section-shadow">
              <div className="grid grid-cols-5 gap-3">
                <KV label="Cases" value={s.hussar_shadow.cases} testId="shadow-cases" />
                <KV label="Blocked" value={s.hussar_shadow.blocked} testId="shadow-blocked" />
                <KV label="Regret" value={`$${s.hussar_shadow.decision_regret_usd}`} testId="shadow-regret" />
                <KV label="Protected" value={`$${s.hussar_shadow.capital_protected_usd}`} testId="shadow-protected" />
                <KV label="Foregone" value={`$${s.hussar_shadow.opportunity_foregone_usd}`} testId="shadow-foregone" />
              </div>
            </Panel>

            <Panel title="Strategy & Regime Learning" testId="section-strategy-learning">
              <div className="grid grid-cols-3 gap-3 mb-2">
                <KV label="Promotions" value={s.strategy_learning.promotions} testId="strat-promotions" />
                <KV label="Rejections" value={s.strategy_learning.rejections} testId="strat-rejections" />
                <KV label="Transitions" value={s.strategy_learning.lifecycle_transitions} testId="strat-transitions" />
              </div>
              <List testId="regime-learning-list"
                items={Object.entries(s.regime_learning).map(([reg, v]) =>
                  `${reg}: ${v.cases} cases · ${v.blocked} blocked · regret $${v.regret_usd} · protected $${v.capital_protected_usd}`)} />
            </Panel>

            <Panel title="Rejected Ideas (preserved)" testId="section-rejected">
              <KV label="Rejected this period" value={s.rejected_ideas.count} testId="rejected-count" />
              <div className="mt-2"><List items={s.rejected_ideas.titles} testId="rejected-titles" /></div>
            </Panel>

            <Panel title="Data-Quality Alerts" testId="section-data-quality">
              <List testId="dq-alerts"
                items={[...s.data_quality_alerts.stale_sources.map((x) => `STALE source: ${x}`),
                        ...Object.entries(s.data_quality_alerts.event_counts).map(([k, v]) => `${k}: ${v}`)]} />
            </Panel>

            <Panel title="Research Queue — what is next" testId="section-queue-next">
              <KV label="Open items" value={s.research_queue_next.open} testId="queue-open" />
              <div className="mt-2"><List items={s.research_queue_next.titles} testId="queue-titles" /></div>
            </Panel>

            <Panel title="Confidence Trend (dimensioned, period-over-period)" testId="section-confidence">
              <div className="grid grid-cols-3 gap-3">
                <KV label="Experiments completed" value={s.confidence_trend.experiments_completed.join(" → ")} testId="trend-experiments" />
                <KV label="Evidence-backed claims" value={s.confidence_trend.evidence_backed_claims.join(" → ")} testId="trend-claims" />
                <KV label="Direction" value={s.confidence_trend.direction} testId="trend-direction" />
              </div>
              <p className="text-[10px] font-mono text-slate-600 mt-2">{s.confidence_trend.basis}</p>
            </Panel>
          </div>

          <Panel title="Report History" testId="report-history">
            <div className="space-y-1 text-[10px] font-mono text-slate-500">
              {history.map((h) => (
                <div key={h.id} data-testid={`report-history-${h.id}`}>
                  {h.generated_at.slice(0, 16)} · {h.period} · by {h.generated_by}</div>))}
              {history.length === 0 && <span>none</span>}
            </div>
          </Panel>
        </>)}
    </div>
  );
}
